import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FaReact, 
  FaNodeJs, 
  FaPython, 
  FaDatabase, 
  FaHtml5, 
  FaCss3Alt, 
  FaBootstrap,
  FaGitAlt,
  FaGithub,
  FaFigma,
  FaPenNib,
  FaPalette,
  FaChartLine,
  FaServer,
  FaMobile,
  FaCode,
  FaTerminal,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';
import { 
  SiTypescript, 
  SiJavascript, 
  SiMongodb, 
  SiTailwindcss, 
  SiExpress,
  SiPostgresql,
  SiFirebase,
  SiDocker,
  SiPostman,
  SiGraphql,
  SiRedux,
  SiNextdotjs,
    SiAdobephotoshop,
  SiAdobeillustrator,
  SiFramer,
  SiDjango
} from 'react-icons/si';

// Define icon props interface
interface IconProps {
  className?: string;
}

// Type for icon components
type IconType = React.FC<IconProps>;

interface Skill {
  icon: IconType;
  name: string;
  level: number;
  color: string;
  category: string;
}

const SKILLS_PER_PAGE = 9;

const skills: Skill[] = [
  // Frontend Technologies
  { icon: FaReact as IconType, name: 'React.js', level: 85, color: 'text-cyan-400', category: 'frontend' },
  { icon: SiNextdotjs as IconType, name: 'Next.js', level: 80, color: 'text-gray-300', category: 'frontend' },
  { icon: SiJavascript as IconType, name: 'JavaScript', level: 90, color: 'text-yellow-400', category: 'frontend' },
  { icon: SiTypescript as IconType, name: 'TypeScript', level: 75, color: 'text-blue-500', category: 'frontend' },
  { icon: FaHtml5 as IconType, name: 'HTML5', level: 95, color: 'text-orange-500', category: 'frontend' },
  { icon: FaCss3Alt as IconType, name: 'CSS3/SASS', level: 90, color: 'text-blue-400', category: 'frontend' },
  { icon: SiTailwindcss as IconType, name: 'Tailwind CSS', level: 85, color: 'text-cyan-400', category: 'frontend' },
  { icon: FaBootstrap as IconType, name: 'Bootstrap', level: 85, color: 'text-purple-400', category: 'frontend' },
  { icon: SiRedux as IconType, name: 'Redux', level: 35, color: 'text-purple-500', category: 'frontend' },
  
  // Backend Technologies
  { icon: FaNodeJs as IconType, name: 'Node.js', level: 75, color: 'text-green-500', category: 'backend' },
  { icon: SiExpress as IconType, name: 'Express.js', level: 75, color: 'text-gray-300', category: 'backend' },
  { icon: FaPython as IconType, name: 'Python', level: 70, color: 'text-blue-400', category: 'backend' },
  { icon: SiGraphql as IconType, name: 'GraphQL', level: 75, color: 'text-pink-500', category: 'backend' },
  { icon: SiDjango as IconType, name: 'Django', level: 60, color: 'text-green-600', category: 'backend' },
  { icon: FaServer as IconType, name: 'REST APIs', level: 85, color: 'text-green-400', category: 'backend' },
  { icon: FaMobile as IconType, name: 'RESTful APIs', level: 85, color: 'text-blue-300', category: 'backend' },
  
  // Database Technologies
  { icon: SiMongodb as IconType, name: 'MongoDB', level: 80, color: 'text-green-500', category: 'database' },
  { icon: FaDatabase as IconType, name: 'MySQL', level: 90, color: 'text-blue-600', category: 'database' },
  { icon: SiPostgresql as IconType, name: 'PostgreSQL', level: 75, color: 'text-blue-400', category: 'database' },
  { icon: SiFirebase as IconType, name: 'Firebase', level: 50, color: 'text-yellow-500', category: 'database' },
  { icon: FaDatabase as IconType, name: 'Redis', level: 50, color: 'text-red-600', category: 'database' },
  
  // Development Tools & Version Control
  { icon: FaGitAlt as IconType, name: 'Git', level: 90, color: 'text-orange-600', category: 'tools' },
  { icon: FaGithub as IconType, name: 'GitHub', level: 88, color: 'text-gray-300', category: 'tools' },
  { icon: SiDocker as IconType, name: 'Docker', level: 55, color: 'text-blue-500', category: 'tools' },
  { icon: SiPostman as IconType, name: 'Postman', level: 85, color: 'text-orange-400', category: 'tools' },
  { icon: FaTerminal as IconType, name: 'CLI/Terminal', level: 88, color: 'text-green-400', category: 'tools' },
  
  // Design & Graphics Tools
  { icon: FaFigma as IconType, name: 'Figma', level: 82, color: 'text-purple-500', category: 'design' },
  { icon: FaPenNib as IconType, name: 'Adobe XD', level: 70, color: 'text-pink-500', category: 'design' },
  { icon: SiAdobephotoshop as IconType, name: 'Photoshop', level: 85, color: 'text-blue-400', category: 'design' },
  { icon: SiAdobeillustrator as IconType, name: 'Illustrator', level: 65, color: 'text-orange-400', category: 'design' },
  { icon: FaPalette as IconType, name: 'UI/UX Design', level: 85, color: 'text-green-400', category: 'design' },
  { icon: SiFramer as IconType, name: 'Framer Motion', level: 70, color: 'text-pink-400', category: 'design' },
  { icon: FaChartLine as IconType, name: 'Data Viz', level: 80, color: 'text-blue-300', category: 'design' },
];

const Skills: React.FC = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [activeCategory, setActiveCategory] = useState('all');
  const [hoveredSkill, setHoveredSkill] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory]);

  // Skill Categories
  const categories = [
    { id: 'all', name: 'All Skills', count: 33 },
    { id: 'frontend', name: 'Frontend', count: 9 },
    { id: 'backend', name: 'Backend', count: 7 },
    { id: 'database', name: 'Database', count: 5 },
    { id: 'tools', name: 'Dev Tools', count: 5 },
    { id: 'design', name: 'Design', count: 7 }
  ];

  // Filter skills based on active category
  const filteredSkills = useMemo(() => {
    return activeCategory === 'all' 
      ? skills 
      : skills.filter(skill => skill.category === activeCategory);
  }, [activeCategory]);

  // Pagination logic
  const totalPages = Math.ceil(filteredSkills.length / SKILLS_PER_PAGE);
  const startIndex = (currentPage - 1) * SKILLS_PER_PAGE;
  const endIndex = startIndex + SKILLS_PER_PAGE;
  const currentSkills = filteredSkills.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to skills section when changing pages
    const skillsSection = document.getElementById('skills');
    if (skillsSection) {
      skillsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12
      }
    }
  };

  const getProficiencyLabel = (level: number) => {
    if (level >= 90) return 'Expert';
    if (level >= 80) return 'Advanced';
    if (level >= 70) return 'Intermediate';
    return 'Beginner';
  };

  const getProficiencyColor = (level: number) => {
    if (level >= 90) return 'from-green-500 to-emerald-500';
    if (level >= 80) return 'from-cyan-500 to-blue-500';
    if (level >= 70) return 'from-yellow-500 to-orange-500';
    return 'from-purple-500 to-pink-500';
  };

  return (
    <section id="skills" className="py-14 md:py-20 bg-gradient-to-b from-tertiary/30 to-primary relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-1/3 left-1/4 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/3 right-1/4 w-64 h-64 bg-secondary/10 rounded-full blur-3xl animate-pulse animation-delay-2000"></div>
      </div>

      {/* Grid Pattern */}
      <div className="absolute inset-0 z-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(to right, #8882 1px, transparent 1px),
                          linear-gradient(to bottom, #8882 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header Section */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: -20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-4 rounded-full bg-secondary/10 border border-border/50">
                {React.createElement(FaCode as React.ComponentType<{ className?: string }>, { className: "text-secondary" })}
            <span className="text-sm font-semibold text-textPrimary">Technical Expertise</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-textPrimary">Skills & </span>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500">
              Technologies
            </span>
          </h2>
          
          <p className="text-lg text-textSecondary max-w-2xl mx-auto">
            comprehensive List of modern technologies and design tools i used to build exceptional digital experiences
          </p>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3 }}
          className="max-w-4xl mx-auto mb-10"
        >
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`group relative px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 flex items-center gap-2
                  ${activeCategory === cat.id
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg'
                    : 'bg-tertiary/50 border border-border/50 text-textSecondary hover:text-textPrimary hover:border-secondary/50'
                  }`}
              >
                {cat.name}
                <span className={`text-xs px-2 py-0.5 rounded-full ${activeCategory === cat.id ? 'bg-white/20' : 'bg-border/30'}`}>
                  {cat.count}
                </span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Skills Grid - 9 items per page */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="max-w-6xl mx-auto"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentSkills.map((skill, index) => {
              const IconComponent = skill.icon;
              return (
                <motion.div
                  key={`${skill.name}-${currentPage}`}
                  variants={itemVariants}
                  whileHover={{ y: -5, scale: 1.02 }}
                  onMouseEnter={() => setHoveredSkill(skill.name)}
                  onMouseLeave={() => setHoveredSkill(null)}
                  className="group relative p-4 rounded-xl bg-primary/60 border border-border/40 backdrop-blur-sm hover:border-secondary/40 transition-all duration-300"
                >
                  {/* Skill Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${getProficiencyColor(skill.level)} flex items-center justify-center shadow-lg`}>
                        <IconComponent className="text-white text-lg" />
                      </div>
                      <div>
                        <h4 className="font-bold text-textPrimary text-sm">{skill.name}</h4>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-textSecondary">{skill.category}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-border/30">
                            {getProficiencyLabel(skill.level)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-lg font-bold text-textPrimary">
                      {skill.level}%
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="relative">
                    <div className="h-2 w-full bg-border/30 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={inView ? { width: `${skill.level}%` } : { width: 0 }}
                        transition={{ duration: 1.5, delay: index * 0.05, ease: "easeOut" }}
                        className={`h-full rounded-full bg-gradient-to-r ${getProficiencyColor(skill.level)}`}
                      />
                    </div>
                    
                    {/* Hover Effect Line */}
                    <div 
                      className="absolute top-0 h-2 rounded-full bg-gradient-to-r from-blue-400/30 to-purple-400/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      style={{ width: `${skill.level}%` }}
                    />
                  </div>

                  {/* Hover Details */}
                  {hoveredSkill === skill.name && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 rounded-lg bg-primary border border-purple-500/30 text-textPrimary text-xs whitespace-nowrap z-50 shadow-xl"
                    >
                      <div className="font-semibold mb-1">{skill.name}</div>
                      <div className="text-purple-400">{skill.level}% Proficiency</div>
                      <div className="triangle absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45 border-r border-b border-purple-500/30"></div>
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex flex-col items-center justify-center gap-4 mt-12"
          >
            {/* Page Info */}
            <div className="text-center mb-2">
              <span className="text-sm text-textSecondary">
                Page <span className="text-textPrimary font-semibold">{currentPage}</span> of{' '}
                <span className="text-textPrimary font-semibold">{totalPages}</span>
              </span>
            </div>

            {/* Pagination Buttons */}
            <div className="flex items-center justify-center gap-3">
              {/* Previous Button */}
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-all duration-300
                  ${currentPage === 1
                    ? 'opacity-50 cursor-not-allowed bg-tertiary/50 border border-border/50 text-textSecondary'
                    : 'bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:shadow-lg'
                  }`}
              >
                {React.createElement(FaChevronLeft as React.ComponentType<{ className?: string }>, { className: "text-sm" })}
                Previous
              </button>

              {/* Page Numbers */}
              <div className="flex items-center gap-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`w-10 h-10 rounded-full text-sm font-semibold transition-all duration-300
                      ${currentPage === page
                        ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg'
                        : 'bg-tertiary/50 border border-border/50 text-textSecondary hover:text-textPrimary hover:border-secondary/50'
                      }`}
                  >
                    {page}
                  </button>
                ))}
              </div>

              {/* Next Button */}
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-all duration-300
                  ${currentPage === totalPages
                    ? 'opacity-50 cursor-not-allowed bg-tertiary/50 border border-border/50 text-textSecondary'
                    : 'bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:shadow-lg'
                  }`}
              >
                Next
                {React.createElement(FaChevronRight as React.ComponentType<{ className?: string }>, { className: "text-sm" })}
              </button>
            </div>

            {/* Quick Page Jumps */}
            <div className="flex items-center gap-2 mt-4">
              {totalPages > 5 && (
                <>
                  {currentPage > 3 && (
                    <button
                      onClick={() => handlePageChange(1)}
                      className="px-3 py-1.5 text-xs rounded-full bg-tertiary/50 border border-border/50 text-textSecondary hover:text-textPrimary"
                    >
                      1
                    </button>
                  )}
                  {currentPage > 4 && (
                    <span className="text-textSecondary">...</span>
                  )}
                  {[currentPage - 1, currentPage, currentPage + 1]
                    .filter(page => page >= 1 && page <= totalPages)
                    .map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-1.5 text-xs rounded-full ${currentPage === page
                          ? 'bg-purple-500 text-white'
                          : 'bg-tertiary/50 border border-border/50 text-textSecondary hover:text-textPrimary'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  {currentPage < totalPages - 3 && (
                    <span className="text-textSecondary">...</span>
                  )}
                  {currentPage < totalPages - 2 && (
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      className="px-3 py-1.5 text-xs rounded-full bg-tertiary/50 border border-border/50 text-textSecondary hover:text-textPrimary"
                    >
                      {totalPages}
                    </button>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.5; }
        }
        .animate-pulse {
          animation: pulse 2s ease-in-out infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .triangle {
          width: 0;
          height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-top: 6px solid var(--color-bg-primary);
        }
      `}</style>
    </section>
  );
};

export default Skills;